import { serializeUIAiConfigFilterForBrowser } from '@gonfalon/ai-configs/filters';
import { serializeUIContextsViewForBrowser } from '@gonfalon/contexts/kinds/serializeUIContextsViewForBrowser';
import { serializeUIFlagListViewForBrowser } from '@gonfalon/flag-filters/serializeUIFlagListViewForBrowser';
import {
  toAIConfigShortcut,
  toAIConfigsShortcut,
  toContextsShortcut,
  toFlagsShortcut,
  toFlagTargetingShortcut,
  toSegmentsShortcut,
  toViewDetail,
} from '@gonfalon/navigator/navigators';
import type { schemas } from '@gonfalon/openapi/types';
import { shortcutsList } from '@gonfalon/rest-api/queries/shortcuts';
import type { ShortcutsPostRep } from '@gonfalon/rest-api/types';
import { serializeUISegmentsForBrowser } from '@gonfalon/segments/serializeUISegmentsForBrowser';
import { queryOptions } from '@tanstack/react-query';

import { parseShortcut } from './types';

function selectShortcuts(data: schemas['ShortcutCollectionRep']) {
  return data.items.map((shortcut) => transformShortcutForUI({ shortcut })).filter((s) => s !== undefined);
}

export function getShortcutUIQuery({ projectKey }: { projectKey: string }) {
  return queryOptions({
    ...shortcutsList({ projectKey }),
    select: selectShortcuts,
  });
}

function transformShortcutForUI({ shortcut: rep }: { shortcut: ShortcutsPostRep }) {
  const shortcut = parseShortcut(rep);
  if (!shortcut.success) {
    return undefined;
  }

  switch (shortcut.data.type) {
    case 'flag':
      return {
        ...shortcut.data,
        path: toFlagTargetingShortcut({
          projectKey: shortcut.data.context.projectKey,
          flagKey: shortcut.data.entityKey,
          environmentKey: shortcut.data.context.selectedEnvironmentKey,
          environmentKeys: shortcut.data.context.environmentKeys,
        }),
      };
    case 'flags':
      return {
        ...shortcut.data,
        path: toFlagsShortcut(
          {
            projectKey: shortcut.data.context.projectKey,
            environmentKey: shortcut.data.context.selectedEnvironmentKey,
            environmentKeys: shortcut.data.context.environmentKeys,
          },
          {
            search: shortcut.data.filters
              ? serializeUIFlagListViewForBrowser(shortcut.data.filters)
              : new URLSearchParams(),
          },
        ),
      };
    case 'contexts':
      return {
        ...shortcut.data,
        path: toContextsShortcut(
          {
            projectKey: shortcut.data.context.projectKey,
            environmentKey: shortcut.data.context.selectedEnvironmentKey,
            environmentKeys: shortcut.data.context.environmentKeys,
          },
          {
            search: shortcut.data.filters
              ? serializeUIContextsViewForBrowser(shortcut.data.filters)
              : new URLSearchParams(),
          },
        ),
      };
    case 'segments':
      return {
        ...shortcut.data,
        path: toSegmentsShortcut(
          {
            projectKey: shortcut.data.context.projectKey,
            environmentKey: shortcut.data.context.selectedEnvironmentKey,
            environmentKeys: shortcut.data.context.environmentKeys,
          },
          {
            search: shortcut.data.filters
              ? serializeUISegmentsForBrowser(shortcut.data.filters)
              : new URLSearchParams(),
          },
        ),
      };
    case 'view':
      return {
        ...shortcut.data,
        path: toViewDetail({
          projectKey: shortcut.data.context.projectKey,
          viewKey: shortcut.data.entityKey,
        }),
      };
    case 'ai-configs':
      return {
        ...shortcut.data,
        path: toAIConfigsShortcut(
          {
            projectKey: shortcut.data.context.projectKey,
            environmentKey: shortcut.data.context.selectedEnvironmentKey,
            environmentKeys: shortcut.data.context.environmentKeys,
          },
          {
            search: shortcut.data.filters
              ? serializeUIAiConfigFilterForBrowser(shortcut.data.filters)
              : new URLSearchParams(),
          },
        ),
      };
    case 'ai-config':
      return {
        ...shortcut.data,
        path: toAIConfigShortcut({
          projectKey: shortcut.data.context.projectKey,
          configKey: shortcut.data.entityKey,
          environmentKey: shortcut.data.context.selectedEnvironmentKey,
          environmentKeys: shortcut.data.context.environmentKeys,
        }),
      };
    default:
      return undefined;
  }
}
